import React from 'react';
import PropTypes from 'prop-types';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import {styled} from '@mui/system';

import {concatUrl} from '../helpers/url';
import {getCall} from '../helpers/request';
import GlobalContext from '../helpers/context';
import {checkUserRoles} from '../helpers/authentication';
import {getUrlParams} from '../helpers/location';
import ContentTabs from '../component/ContentTabs';
import ProgressSpinner from '../component/atoms/ProgressSpinner';
import ResearcherId from '../component/molecule/ResearcherId';
import NameVariants from '../component/molecule/NameVariants';
import Organization from '../component/molecule/Organization';
import DocumentStats from '../component/molecule/DocumentStats';
import GoogleLink from '../component/molecule/GoogleLink';
import CurationProfileMerge from './CurationProfileMerge';
import CurationProfileSplit from './CurationProfileSplit';
import CurationProfileIgnored from './CurationProfileIgnored';
import CurationProfileHistory from './CurationProfileHistory';
import CurationProfileDocSearch from './CurationProfileDocSearch';
import CurationProfileData from './CurationProfileData';
import CurationProfileMetadata from './CurationProfileMetadata';
import CurationProfileStatusDropDown from './CurationProfileStatusDropDown';


const StyledDetailsHeader = styled(Paper)(({ theme }) =>({
    paddingLeft: theme.custom.paper.side_space,
    paddingRight: theme.custom.paper.side_space,
    paddingTop: theme.custom.paper.top_space,
    paddingBottom: theme.custom.paper.bottom_space
}));

const StyledDetailsHeaderMetadata = styled('div')(({ theme }) =>({
    marginTop: 10,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    lineHeight: theme.custom.font.line_height
}));

const StyledStatusGrid = styled(Grid)({
    textAlign: 'right'
});


class CurationProfileDetails extends React.Component {

    constructor(props) {
        super(props);

        this.params = getUrlParams();
                
        this.state = {
            curation_profile_id: this.params.id,
            curation_profile: {}
        };

        this.is_mounted = false;
        this.accepted_roles = ['admin', 'manager', 'reviewer'];
        this.researcher_base_data_url = concatUrl(window.ROOT_URL, 'api', 'curation_profile', 'base_data');

    }

    componentDidMount(){
        this.is_mounted = true;
        this.props.updatedCallback();
        if (this.state.curation_profile_id && this.params.workspace_id){
            this.loadCurationProfileDetails(this.state.curation_profile_id);
        }else{
            this.context.checkUserAccess(this.conditionCallback);
        }
    }

    componentWillUnmount(){
        this.is_mounted = false;
    }

    componentDidUpdate(prevProps){
        this.props.updatedCallback();
        if(this.params.id && this.params.id !== this.state.curation_profile_id){
            this.reloadCurationProfileDetails(this.params.id);
        }
    }

    conditionCallback = (user) => {
        return checkUserRoles(user, this.accepted_roles);
    };

    stopPropagation = (event) => {
        event.stopPropagation();
    };

    reloadCurationProfileDetails = (curation_profile_id) => {
        if(curation_profile_id){
            this.setState({
                curation_profile_id: curation_profile_id
            });
            this.loadCurationProfileDetails(curation_profile_id);
        }
    };


    // +++ load curation profile data +++++++++++++++++++++++++++++++++++++++++++++++

    loadCurationProfileDetailsFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not load curation profile details.');
    };

    loadCurationProfileDetailsDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if (!data) {
            callback_failure();
        }else {
            this.setState({curation_profile: data});
        }
    };

    loadCurationProfileDetails = (curation_profile_id) => {
        getCall(`${this.researcher_base_data_url}?id=${curation_profile_id}&workspace_id=${this.params.workspace_id}`,
            this.loadCurationProfileDetailsDone,
            this.loadCurationProfileDetailsFailed);
    };

    organizationInfo = () => {
        if (this.state.curation_profile.current_organization){
            return (
                <div>
                    <Organization organization={this.state.curation_profile.current_organization} docDetailsStyle={true}/>
                </div>
            );
        }
    };

    researcherInfo = () => {
        if (this.state.curation_profile.researcher_id){
            return (
                <div>
                    <ResearcherId id={this.state.curation_profile.researcher_id} />
                </div>
            );
        }
    };

    googleSearchLink = () => {
        let query = this.state.curation_profile.full_name;
        if (this.state.curation_profile.current_organization){
            query += ' ' + this.state.curation_profile.current_organization.name;
        }
        return (
            <div>
                <GoogleLink query={query} />
            </div>
        );
    };

    curationProfileHeader(){
        return(
            <StyledDetailsHeader>
                <Grid container justifyContent="space-between">
                    <Grid size={3}>
                        <Typography variant="h5">
                            {this.state.curation_profile.full_name}
                        </Typography>
                        <NameVariants variants={this.state.curation_profile.name_variants} />
                    </Grid>
                    <Grid size={4}>
                        <StyledDetailsHeaderMetadata>
                            {this.organizationInfo()}
                            {this.researcherInfo()}
                            {this.googleSearchLink()}
                        </StyledDetailsHeaderMetadata>
                    </Grid>
                    <Grid size={3}>
                        <StyledDetailsHeaderMetadata>
                            <DocumentStats profile={this.state.curation_profile.doc_stats} showLabel={true} showVerified={true}/>
                        </StyledDetailsHeaderMetadata>
                    </Grid>
                    <StyledStatusGrid size={2}>
                        <CurationProfileStatusDropDown
                            curation_profile_id={parseInt(this.state.curation_profile_id)}
                            workspace_id={parseInt(this.params.workspace_id)}
                            metadata={this.state.curation_profile.metadata} />
                    </StyledStatusGrid>
                </Grid>
            </StyledDetailsHeader>
        );
    }

    initCurationProfileMerge = () => {
        // init CurationProfileMerge after CP data (last_name) are loaded
        return this.state.curation_profile.last_name && <CurationProfileMerge
            last_name={this.state.curation_profile.last_name}
            first_name={this.state.curation_profile.first_name}
            current_profile_id={parseInt(this.state.curation_profile.profile_id)}
            curation_profile_id={parseInt(this.state.curation_profile_id)}
            workspace_id={parseInt(this.params.workspace_id)}
            mergeCallback={this.reloadCurationProfileDetails}/>;
    };

    initCurationProfileSplit = () => {
        return <CurationProfileSplit curation_profile_id={parseInt(this.state.curation_profile_id)}
                                     workspace_id={parseInt(this.params.workspace_id)}
                                     splitCallback={this.reloadCurationProfileDetails}/>;
    };

    initCurationProfileData = () => {
        return <CurationProfileData curation_profile_id={parseInt(this.state.curation_profile_id)}
                                    workspace_id={parseInt(this.params.workspace_id)}/>;
    };

    initCurationProfileIgnored = () => {
        return <CurationProfileIgnored curation_profile_id={parseInt(this.state.curation_profile_id)}
                                       workspace_id={parseInt(this.params.workspace_id)}/>;
    };

    initCurationProfileHistory = () => {
        return <CurationProfileHistory curation_profile_id={parseInt(this.state.curation_profile_id)}
                                       workspace_id={parseInt(this.params.workspace_id)}/>;
    };

    initCurationProfileDocSearch = () => {
        return this.state.curation_profile.last_name && <CurationProfileDocSearch
            current_profile_id={parseInt(this.state.curation_profile.profile_id)}
            curation_profile_id={parseInt(this.state.curation_profile_id)}
            workspace_id={parseInt(this.params.workspace_id)}
            doc_id={this.params.doc_id}
            last_name={this.state.curation_profile.last_name}
            mergeCallback={this.reloadCurationProfileDetails}/>;
    };


    curationProfileContent = () => {

        const tabs = [
            {
                label: 'Merge',
                content: this.initCurationProfileMerge
            },
            {
                label: 'Split/Verify',
                content: this.initCurationProfileSplit
            },
            {
                label: 'Details',
                content: this.initCurationProfileData
            },
            {
                label: 'Ignored Profiles',
                content: this.initCurationProfileIgnored
            },
            {
                label: 'History',
                content: this.initCurationProfileHistory
            },
            {
                label: 'Document Search',
                content: this.initCurationProfileDocSearch
            }
        ];

        if (this.state.curation_profile.metadata) {
            return (
                <div>
                    {this.curationProfileHeader()}
                    <CurationProfileMetadata curation_profile_id={parseInt(this.state.curation_profile_id)}
                        workspace_id={parseInt(this.params.workspace_id)}
                        metadata={this.state.curation_profile.metadata}/>
                    <ContentTabs tabs={tabs} default={parseInt(this.params.default_tab)}/>
                </div>
            );
        }
        return (
            <ProgressSpinner />
        );
    };

    render() {

        return (
            <GlobalContext.Consumer>{() => (
                <div data-bt="curation_profile_details">
                    {this.curationProfileContent()}
                </div>
            )}
            </GlobalContext.Consumer>
        );

    }
}

CurationProfileDetails.contextType = GlobalContext;

CurationProfileDetails.propTypes = {
    updatedCallback: PropTypes.func
};

export default CurationProfileDetails;
